import { FC, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Button } from "@src/lib/ui/Button";
import { Progress } from "@src/lib/ui/Progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import MediaPauseOutlineIcon from "@src/lib/icons/18px/media-pause-outline";
import MediaPlayFillIcon from "@src/lib/icons/18px/media-play-fill";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import useUser from "@src/features/app/hooks/useUser";
import { UserType } from "@src/types.generated";
import LeadVideo from "./LeadVideo";
import {
  featuresFor,
  HELP_CENTER_URL,
  ReleaseHeadlineFeature,
  ReleaseHeadlineRelease,
  releaseUrl,
} from "./releases";

// Plain white surface shared by the letter, the homepage card, and the note
// inside the envelope.
export const RELEASE_PAPER_CLASSNAME = "bg-bg-elevated";

// The selected feature on the right, on a white card: its clip, or the logos
// of the tools it works with.
const FeaturePreview: FC<{
  feature: ReleaseHeadlineFeature;
  paused: boolean;
  onPausedChange: (paused: boolean) => void;
  onEnded: () => void;
  onProgress: (fraction: number) => void;
  showTourControl: boolean;
  // The homepage card puts the feature's line here, under the clip, instead
  // of in the list, so the list stays one line per feature.
  caption: boolean;
}> = ({
  feature,
  paused,
  onPausedChange,
  onEnded,
  onProgress,
  showTourControl,
  caption,
}) => (
  <div className="my-auto w-full rounded-xl bg-bg-primary p-1.5 shadow-extra-large">
    {feature.video ? (
      // Keyed by feature so each one starts its clip from the top; a pause
      // carries over to the next clip.
      <LeadVideo
        key={feature.slug}
        feature={feature}
        startPaused={paused}
        onPausedChange={onPausedChange}
        loop={false}
        onEnded={onEnded}
        onProgress={onProgress}
      />
    ) : (
      feature.logos && (
        <div className="relative">
          <div className="mx-auto grid w-fit grid-cols-2 gap-3 p-6">
            {feature.logos.map((logo) => (
              <span
                key={logo.name}
                className="flex items-center gap-2 rounded-lg border border-border-primary py-2 pr-4 pl-2"
              >
                <img
                  src={logo.src}
                  alt=""
                  width={28}
                  height={28}
                  className="size-7"
                />
                <Text variant="compact-title" className="text-text-primary">
                  {logo.name}
                </Text>
              </span>
            ))}
          </div>
          {/* Its timed turn moves on by itself, so it needs a pause too. */}
          {showTourControl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => onPausedChange(!paused)}
                  aria-label={paused ? "Resume tour" : "Pause tour"}
                  className="absolute bottom-0.5 left-0.5"
                >
                  {paused ? <MediaPlayFillIcon /> : <MediaPauseOutlineIcon />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {paused ? "Resume tour" : "Pause tour"}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )
    )}
    {caption && (
      <Text
        variant="compact-description"
        className="px-2.5 pt-2.5 pb-1.5 text-pretty text-text-secondary"
        multiline
        data-testid="release-preview-caption"
      >
        {feature.deck}
      </Text>
    )}
  </div>
);

// Whether focus came from the keyboard. Browsers without :focus-visible
// (and jsdom) throw on the selector; treat that as not keyboard.
const isKeyboardFocus = (element: Element): boolean => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return false;
  }
};

// Tailwind's md breakpoint, where the list and preview sit side by side.
const MD_QUERY = "(min-width: 48rem)";
// How long a feature without a clip (MCP) stays up before the next one.
const NO_CLIP_DWELL_MS = 6000;

// One feature in the list: its name (and, when selected, its line) over a
// hairline divider. The selected feature's divider fills as its clip plays,
// so the divider is also the loading bar for the preview.
const FeatureCard: FC<{
  feature: ReleaseHeadlineFeature;
  selected: boolean;
  progress: number;
  onSelect: () => void;
  dense: boolean;
}> = ({ feature, selected, progress, onSelect, dense }) => {
  const ref = useRef<HTMLButtonElement>(null);
  // The tour can select a feature scrolled out of a short list; bring it in.
  // Only where the list scrolls on its own (md and up): below that the whole
  // letter scrolls, and this would pull a viewer away from the clip.
  useEffect(() => {
    if (!selected || !window.matchMedia?.(MD_QUERY).matches) return;
    ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [selected]);
  return (
    <div>
      <button
        ref={ref}
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        data-testid={`release-feature-${feature.slug}`}
        className={cn(
          "group flex w-full flex-col gap-1 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-state-focus",
          dense ? "py-2" : "py-3",
        )}
      >
        <Text
          variant="compact-title"
          className={cn(
            "transition-colors",
            selected
              ? "text-text-primary"
              : "text-text-secondary group-hover:text-text-primary",
          )}
        >
          {feature.title}
        </Text>
        {selected && !dense && (
          <Text
            variant="helper-description"
            className="text-pretty text-text-secondary"
            multiline
          >
            {feature.deck}
          </Text>
        )}
      </button>
      <Progress
        aria-hidden
        value={selected ? progress * 100 : 0}
        className="h-px rounded-none bg-border-primary"
        indicatorClassName="bg-action-primary"
      />
    </div>
  );
};

// The letter that unfolds out of the envelope: the headline and the release's
// features as cards on the left, a preview of the selected one on the right.
// It plays through the features in turn, each clip once, then starts over;
// picking a card jumps there, and pausing a clip stops the tour.
const ReleaseLetter: FC<{
  release: ReleaseHeadlineRelease;
  headingId: string;
  // "card": docked on the homepage, a size smaller so it fits a shorter box.
  layout?: "letter" | "card";
}> = ({ release, headingId, layout = "letter" }) => {
  const isCard = layout === "card";
  const { user } = useUser();
  const audience =
    user?.userType === UserType.Manager ? UserType.Manager : UserType.Admin;
  const features = featuresFor(release, audience);
  const [selectedSlug, setSelectedSlug] = useState(features[0]?.slug);
  const selected =
    features.find((feature) => feature.slug === selectedSlug) ?? features[0];
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const reduceMotion = useReducedMotion();
  const select = (slug: string) => {
    endedWhileHolding.current = false;
    setSelectedSlug(slug);
    setProgress(0);
  };
  const advance = () => {
    const index = features.findIndex((feature) => feature === selected);
    select(features[(index + 1) % features.length].slug);
  };
  // The tour holds while a mouse is on the list or keyboard focus is in the
  // letter, so
  // features don't move under the pointer or the keyboard; a clip that ends
  // meanwhile moves on once both leave. Touch doesn't hold: a tap never
  // "leaves".
  const [pointerHolding, setPointerHolding] = useState(false);
  const [focusHolding, setFocusHolding] = useState(false);
  const holding = pointerHolding || focusHolding;
  const endedWhileHolding = useRef(false);
  const releaseHold = (stillHolding: boolean) => {
    if (stillHolding || !endedWhileHolding.current) return;
    endedWhileHolding.current = false;
    if (touring) advance();
  };
  const touring = !reduceMotion && !paused;
  // A feature without a clip gets a fixed turn instead of a clip's length,
  // picking up where it left off after a hold or pause.
  const dwell = touring && !holding && selected && !selected.video;
  const progressRef = useRef(progress);
  progressRef.current = progress;
  useEffect(() => {
    if (!dwell) return;
    const start = Date.now() - progressRef.current * NO_CLIP_DWELL_MS;
    const timer = setInterval(() => {
      const fraction = (Date.now() - start) / NO_CLIP_DWELL_MS;
      if (fraction >= 1) advance();
      else setProgress(fraction);
    }, 100);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per feature
  }, [dwell, selected?.slug]);

  return (
    <div
      // Narrow screens stack the panes and scroll as one; wide screens put
      // them side by side and scroll the list alone.
      className="h-full overflow-y-auto md:grid md:grid-cols-[2fr_3fr] md:overflow-hidden"
      data-testid="release-letter-body"
      // Keyboard focus anywhere in the letter holds the tour, so tabbing from
      // the list to the preview's pause button doesn't advance (and remount)
      // under it. A click also focuses, and shouldn't hold.
      onFocus={(event) => {
        const keyboard = isKeyboardFocus(event.target);
        setFocusHolding(keyboard);
        if (!keyboard) releaseHold(pointerHolding);
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setFocusHolding(false);
        releaseHold(pointerHolding);
      }}
    >
      <div
        className={cn(
          "flex flex-col md:min-h-0 md:overflow-y-auto",
          isCard ? "gap-4 p-5" : "gap-5 p-8",
        )}
      >
        <div className="flex flex-col gap-2">
          <Text variant="compact-title" className="text-text-tertiary">
            {release.month} release
          </Text>
          <h2
            id={headingId}
            className={cn(
              "font-sans-v4 font-semibold-v4 text-balance text-text-primary",
              isCard ? "text-2xl-v4" : "text-3xl-v4",
            )}
          >
            {release.headline}
          </h2>
          <Text
            variant={isCard ? "compact-description" : "body-description"}
            className="text-pretty text-text-secondary"
            multiline
          >
            {release.subline[audience]}
          </Text>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button asChild size="sm">
              <a
                href={releaseUrl(release)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="release-headline-cta"
              >
                Read more
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <a
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="release-headline-help-cta"
              >
                Help articles
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </Button>
          </div>
        </div>
        <div
          className="mt-auto flex flex-col"
          data-testid="release-features"
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") setPointerHolding(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== "mouse") return;
            setPointerHolding(false);
            releaseHold(focusHolding);
          }}
        >
          {features.map((feature) => (
            <FeatureCard
              key={feature.slug}
              feature={feature}
              selected={feature === selected}
              progress={progress}
              onSelect={() => select(feature.slug)}
              dense={isCard}
            />
          ))}
        </div>
      </div>
      {/* Soft brand-color glow behind the floating preview. */}
      <div
        className="relative isolate overflow-hidden bg-bg-secondary md:min-h-0"
        data-testid="release-preview"
      >
        <span
          aria-hidden
          className="absolute -top-1/4 -left-1/4 -z-10 size-3/4 rounded-full bg-brand-sky opacity-70 blur-3xl"
        />
        <span
          aria-hidden
          className="absolute top-0 -right-1/4 -z-10 size-2/3 rounded-full bg-brand-yellow opacity-60 blur-3xl"
        />
        <span
          aria-hidden
          className="absolute -bottom-1/3 left-1/6 -z-10 size-3/4 rounded-full bg-brand-lilac opacity-40 blur-3xl"
        />
        {/* Scrolls when a short window can't fit the card; the card's my-auto
            centers it otherwise. The glow stays put behind it. */}
        <div
          className={cn(
            "flex h-full overflow-y-auto p-6",
            isCard ? "md:p-5" : "md:p-8",
          )}
        >
          {selected && (
            <FeaturePreview
              feature={selected}
              paused={paused}
              onPausedChange={setPaused}
              onEnded={() => {
                if (!touring) return;
                if (holding) endedWhileHolding.current = true;
                else advance();
              }}
              onProgress={setProgress}
              showTourControl={!reduceMotion}
              caption={isCard}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseLetter;
